import React, { useCallback, useEffect, useState } from 'react';
import { PackageIcon, SearchIcon, PlusIcon, AlertTriangleIcon, BarChart3Icon, EditIcon, TrashIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Product } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { useAuth } from '../context/AuthContext';
// SKU auto-increment prefix
const SKU_PREFIX = 'PRD';
type FormData = {
  nombre: string;
  sku: string;
  stock: string;
  precio: string;
  subTotal: string;
  itbis: string;
  proveedor: string;
  fechaVencimiento: string;
};
const emptyForm: FormData = {
  nombre: '',
  sku: '',
  stock: '',
  precio: '',
  subTotal: '',
  itbis: '',
  proveedor: '',
  fechaVencimiento: ''
};
export function InventarioPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const canEdit = user?.permisos?.inventario_editar !== false && user?.rolNombre !== 'Cliente';
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const mapProduct = (row: any): Product => ({
    id: row.id,
    nombre: row.nombre,
    sku: row.sku,
    stock: row.stock,
    stockMinimo: row.stock_minimo ?? 0,
    precio: row.precio,
    subTotal: row.sub_total || 0,
    itbis: row.itbis || 0,
    proveedor: row.proveedor,
    fechaVencimiento: row.fecha_vencimiento || null
  });
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const allProducts: Product[] = [];
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;
      while (hasMore) {
        const {
          data,
          error
        } = await supabaseAdmin.from('products').select('*').order('nombre').range(from, from + PAGE_SIZE - 1);
        if (error) break;
        if (data && data.length > 0) {
          allProducts.push(...data.map(mapProduct));
          from += PAGE_SIZE;
          hasMore = data.length === PAGE_SIZE;
        } else {
          hasMore = false;
        }
      }
      setProducts(allProducts);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  // Auto-refresh when products change in Supabase
  useRealtimeRefresh(['products'], fetchProducts);
  const generateSku = (currentProducts: Product[]) => {
    const existing = currentProducts.map(p => {
      const match = p.sku.match(new RegExp(`^${SKU_PREFIX}-(\\d+)$`, 'i'));
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${SKU_PREFIX}-${String(nextNum).padStart(3, '0')}`;
  };
  const filteredProducts = products.filter(p => p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase()) || p.proveedor.toLowerCase().includes(searchQuery.toLowerCase()));
  // Sorting setup
  const {
    items: sortedProducts,
    requestSort,
    sortConfig
  } = useSortableData(filteredProducts, {
    sku: p => p.sku,
    nombre: p => p.nombre,
    proveedor: p => p.proveedor,
    precio: p => p.precio,
    subTotal: p => p.subTotal || 0,
    itbis: p => p.itbis || 0,
    stock: p => p.stock,
    fechaVencimiento: p => p.fechaVencimiento || ''
  });
  const totalProductos = products.length;
  const sinStock = products.filter(p => p.stock === 0).length;
  const valorInventario = products.reduce((sum, p) => sum + p.stock * p.precio, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const totalPages = Math.ceil(sortedProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = sortedProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      ...emptyForm,
      sku: generateSku(products)
    });
    setIsModalOpen(true);
  };
  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      nombre: product.nombre,
      sku: product.sku,
      stock: String(product.stock),
      precio: String(product.precio),
      subTotal: String(product.subTotal || 0),
      itbis: String(product.itbis || 0),
      proveedor: product.proveedor,
      fechaVencimiento: product.fechaVencimiento || ''
    });
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!formData.nombre || !formData.sku || !formData.precio) return;
    setIsSaving(true);
    try {
      const payload = {
        nombre: formData.nombre,
        sku: formData.sku,
        stock: Number(formData.stock) || 0,
        precio: Number(formData.precio),
        sub_total: Number(formData.subTotal) || 0,
        itbis: Number(formData.itbis) || 0,
        proveedor: formData.proveedor,
        fecha_vencimiento: formData.fechaVencimiento || null
      };
      if (editingProduct) {
        const {
          data
        } = await supabaseAdmin.from('products').update(payload).eq('id', editingProduct.id).select().single();
        if (data) setProducts(prev => prev.map(p => p.id === editingProduct.id ? mapProduct(data) : p));
        addActivityLog('Editó producto', `Producto: ${formData.nombre} | SKU: ${formData.sku} | Stock: ${formData.stock}`);
      } else {
        const {
          data
        } = await supabaseAdmin.from('products').insert(payload).select().single();
        if (data) setProducts(prev => [mapProduct(data), ...prev]);
        addActivityLog('Creó producto', `Producto: ${formData.nombre} | SKU: ${formData.sku} | Precio: RD${formData.precio}`);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingProduct) return;
    addActivityLog('Eliminó producto', `Producto: ${editingProduct.nombre} | SKU: ${editingProduct.sku}`);
    await supabaseAdmin.from('products').delete().eq('id', editingProduct.id);
    setProducts(prev => prev.filter(p => p.id !== editingProduct.id));
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-2261">
      <PageHeader title="Inventario" subtitle={canEdit ? 'Control de stock y productos' : 'Catálogo de productos disponibles'} icon={<PackageIcon className="w-6 h-6" data-id="element-2263" />} data-id="element-2262">
        {canEdit && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2265" />} onClick={openAddModal} data-id="element-2264">
            Nuevo Producto
          </Button>}
      </PageHeader>

      {/* Sin stock alert */}
      {sinStock > 0 && <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-stagger-1" data-id="element-2266">
          <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-2267" />
          <p className="text-sm text-amber-800 font-medium" data-id="element-2268">
            {sinStock} producto{sinStock > 1 ? 's' : ''} sin existencia. Revisa
            el inventario.
          </p>
        </div>}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-2269">
        <StatsCard title="Total Productos" value={totalProductos} icon={<PackageIcon className="w-5 h-5" data-id="element-2271" />} variant="primary" data-id="element-2270" />
        <StatsCard title="Sin Existencia" value={sinStock} icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-2273" />} variant="danger" data-id="element-2272" />
        <StatsCard title="Valor Inventario" value={formatCurrency(valorInventario)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-2275" />} variant="success" data-id="element-2274" />
      </div>

      {/* Search */}
      <div className="relative max-w-md animate-stagger-2" data-id="element-2276">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2277" />
        <input type="text" placeholder="Buscar por código, descripción o proveedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-2278" />
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-2279">
        {filteredProducts.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-2280">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-2281">
              <PackageIcon className="w-8 h-8 text-slate-400" data-id="element-2282" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-2283">
              No hay productos
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-2284">
              No se encontraron productos con los filtros aplicados
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-2285">
            <table className="w-full" data-id="element-2286">
              <thead data-id="element-2287">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-2288">
                  <SortableTableHeader label="Código" sortKey="sku" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2289" />
                  <SortableTableHeader label="Descripción" sortKey="nombre" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2290" />
                  <SortableTableHeader label="Proveedor" sortKey="proveedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2291" />
                  <SortableTableHeader label="Precio" sortKey="precio" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2292" />
                  <SortableTableHeader label="Sub Total" sortKey="subTotal" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2293" />
                  <SortableTableHeader label="ITBIS" sortKey="itbis" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2294" />
                  <SortableTableHeader label="Existencia" sortKey="stock" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2295" />
                  <SortableTableHeader label="Vencimiento" sortKey="fechaVencimiento" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-2296" />
                  {canEdit && <th className="px-5 py-4 w-20" data-id="element-2297"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2298">
                {paginatedProducts.map(product => <tr key={product.id} className={`hover:bg-slate-50/50 transition-colors ${product.stock === 0 ? 'bg-rose-50/20' : ''}`} data-id="element-2299">
                    <td className="px-5 py-4 font-mono text-sm font-semibold text-[#2952C4]" data-id="element-2300">
                      {product.sku}
                    </td>
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-2301">
                      {product.nombre}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2302">
                      {product.proveedor}
                    </td>
                    <td className="px-5 py-4 text-right font-medium text-slate-900" data-id="element-2303">
                      {formatCurrency(product.precio)}
                    </td>
                    <td className="px-5 py-4 text-right text-sm text-slate-700" data-id="element-2304">
                      {formatCurrency(product.subTotal || 0)}
                    </td>
                    <td className="px-5 py-4 text-right text-sm text-slate-700" data-id="element-2305">
                      {formatCurrency(product.itbis || 0)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-2306">
                      <span className={`font-bold text-lg ${product.stock === 0 ? 'text-rose-600' : 'text-slate-700'}`} data-id="element-2307">
                        {product.stock}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600" data-id="element-2308">
                      {product.fechaVencimiento ? (() => {
                  const d = new Date(product.fechaVencimiento + 'T00:00:00');
                  const now = new Date();
                  const diffDays = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                  const formatted = d.toLocaleDateString('es-DO', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  });
                  if (diffDays < 0) return <span className="text-rose-600 font-semibold" data-id="element-2309">
                                ⚠ {formatted}
                              </span>;
                  if (diffDays <= 90) return <span className="text-amber-600 font-semibold" data-id="element-2310">
                                {formatted}
                              </span>;
                  return <span data-id="element-2311">{formatted}</span>;
                })() : <span className="text-slate-300" data-id="element-2312">—</span>}
                    </td>
                    {canEdit && <td className="px-5 py-4" data-id="element-2313">
                        <div className="flex items-center gap-1" data-id="element-2314">
                          <button onClick={() => openEditModal(product)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-2315">
                            <EditIcon className="w-4 h-4" data-id="element-2316" />
                          </button>
                          <button onClick={() => {
                    setEditingProduct(product);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2317">
                            <TrashIcon className="w-4 h-4" data-id="element-2318" />
                          </button>
                        </div>
                      </td>}
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      <div className="animate-stagger-4" data-id="element-2319">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredProducts.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-2320" />
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" data-id="element-2321">
        <ModalBody data-id="element-2322">
          <div className="space-y-4" data-id="element-2323">
            <div className="grid grid-cols-2 gap-4" data-id="element-2324">
              <Input label="Código (SKU)" value={formData.sku} onChange={e => setFormData({
              ...formData,
              sku: e.target.value
            })} placeholder="PRD-001" data-id="element-2325" />
              <Input label="Proveedor" value={formData.proveedor} onChange={e => setFormData({
              ...formData,
              proveedor: e.target.value
            })} placeholder="Ej: Laboratorios RD" data-id="element-2326" />
            </div>
            <Input label="Descripción" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} placeholder="Ej: Amoxicilina 500mg" data-id="element-2327" />
            <div className="grid grid-cols-2 gap-4" data-id="element-2328">
              <Input label="Precio (DOP)" type="number" value={formData.precio} onChange={e => setFormData({
              ...formData,
              precio: e.target.value
            })} placeholder="0" data-id="element-2329" />
              <Input label="Existencia" type="number" value={formData.stock} onChange={e => setFormData({
              ...formData,
              stock: e.target.value
            })} placeholder="0" data-id="element-2330" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2331">
              <Input label="Sub Total (DOP)" type="number" value={formData.subTotal} onChange={e => setFormData({
              ...formData,
              subTotal: e.target.value
            })} placeholder="0" data-id="element-2332" />
              <Input label="ITBIS (DOP)" type="number" value={formData.itbis} onChange={e => setFormData({
              ...formData,
              itbis: e.target.value
            })} placeholder="0" data-id="element-2333" />
            </div>
            <Input label="Fecha de Vencimiento" type="date" value={formData.fechaVencimiento} onChange={e => setFormData({
            ...formData,
            fechaVencimiento: e.target.value
          })} data-id="element-2334" />
          </div>
        </ModalBody>
        <ModalFooter data-id="element-2335">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-2336">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.nombre || !formData.sku || !formData.precio} data-id="element-2337">
            {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Producto" size="sm" data-id="element-2338">
        <ModalBody data-id="element-2339">
          <p className="text-slate-600" data-id="element-2340">
            ¿Eliminar <strong data-id="element-2341">{editingProduct?.nombre}</strong>? Esta acción no
            se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-2342">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-2343">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-2344">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}